"use client";

import {
  cn,
  Icon,
  Pagination,
  paginationControlVariants,
  PaginationEllipsis,
} from "@to/ui";

import { AgentCard, type AgentCardAgent } from "./agent-card";
import { AgentEmptyState } from "./agent-empty-state";
import { pageWindow } from "@/lib/page-window";
import type { AdAgentResultsPhase } from "@/lib/data/agents";

interface AgentResultsProps {
  agents: AgentCardAgent[];
  total: number;
  page: number;
  totalPages: number;
  /** Items per page — drives the "showing X–Y of Z" line. Default 12. */
  pageSize?: number;
  phase?: AdAgentResultsPhase;
  phasePage?: number;
  phaseOffset?: number;
  journeyOffset?: number;
  featuredTotal?: number;
  compactTotal?: number;
  onPageChange: (page: number) => void | boolean;
  pageHref?: (page: number) => string;
  resetHref?: string;
  agentDetailPath?: string;
}

/**
 * Results region for Find Your Agent (v1 styling pass): a count line, the
 * responsive `AgentCard` grid (1/2/4/5 cols — 5 across on xl, TRU-35) or the
 * empty state, and numbered pagination (first/prev · window+ellipses ·
 * next/last, 20 per page = 4 rows of 5).
 *
 * The component is presentational — pagination + the current page come from
 * props; clicking a page fires `onPageChange`. The v0 list/map view toggle is
 * gone (the v1 design has none, and `synced_locations` has no coordinates so
 * the map branch could only ever render its fallback note). All colors via
 * `--brand-*`; icons via `Icon`.
 */
export function AgentResults({
  agents,
  total,
  page,
  totalPages,
  pageSize = 12,
  phase = "featured",
  phasePage = page,
  phaseOffset = (phasePage - 1) * pageSize,
  featuredTotal = 0,
  compactTotal = 0,
  journeyOffset =
    phase === "featured" ? phaseOffset : featuredTotal + phaseOffset,
  onPageChange,
  pageHref = (pageNumber) => `?page=${pageNumber}`,
  resetHref,
  agentDetailPath = "/agents",
}: AgentResultsProps) {
  const featuredAgents = agents.filter((agent) => agent.isPro);
  const compactAgents = agents.filter((agent) => !agent.isPro);
  const isMixedPage = featuredAgents.length > 0 && compactAgents.length > 0;

  return (
    <div
      data-agent-phase={phase}
      data-phase-page={phasePage}
      data-featured-total={featuredTotal}
      data-compact-total={compactTotal}
      data-mixed-page={isMixedPage || undefined}
    >
      {/* Count line */}
      <p aria-live="polite" aria-atomic="true" className="mb-6 text-sm" style={{ color: "var(--brand-text-muted)" }}>
        {total > 0 ? (
          <>
            Showing{" "}
            <span style={{ color: "var(--brand-text)", fontWeight: 600 }}>
              {journeyOffset + 1}
              {"–"}
              {Math.min(journeyOffset + agents.length, total)}
            </span>{" "}
            of{" "}
            <span style={{ color: "var(--brand-text)", fontWeight: 600 }}>
              {total.toLocaleString("en-US")}
            </span>{" "}
            {total === 1 ? "agent" : "agents"}
          </>
        ) : (
          "No agents"
        )}
      </p>

      {agents.length === 0 ? (
        <div>
          <AgentEmptyState />
          {resetHref && <a className="block text-center underline focus-visible:outline" href={resetHref}>Clear search and filters</a>}
        </div>
      ) : (
        <div className="space-y-8">
          {featuredAgents.length > 0 && (
            <AgentGridSection
              heading="Featured Pro Agents"
              headingId="featured-agent-results-heading"
              agents={featuredAgents}
              variant="featured"
              testId="agent-results-grid"
              agentDetailPath={agentDetailPath}
            />
          )}
          {compactAgents.length > 0 && (
            <AgentGridSection
              heading="All Agents"
              headingId="compact-agent-results-heading"
              agents={compactAgents}
              variant="compact"
              testId={
                featuredAgents.length > 0
                  ? "compact-agent-results-grid"
                  : "agent-results-grid"
              }
              agentDetailPath={agentDetailPath}
            />
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && agents.length > 0 && (
        <Pagination className="mt-10 gap-1.5">
          <PageButton
            label="First page"
            icon="first_page"
            disabled={page === 1}
            href={pageHref(1)}
            onClick={() => onPageChange(1)}
          />
          <PageButton
            label="Previous page"
            icon="chevron_left"
            disabled={page === 1}
            href={pageHref(Math.max(1, page - 1))}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          />
          <div className="flex items-center gap-1">
            {pageWindow(page, totalPages).map((p, idx) =>
              p === "ellipsis" ? (
                <PaginationEllipsis
                  key={`e${idx}`}
                  className="min-h-11 min-w-11"
                  style={{ color: "var(--brand-text-muted)" }}
                />
              ) : (
                <PageLink
                  key={p}
                  href={pageHref(p)}
                  onClick={() => onPageChange(p)}
                  aria-label={`Page ${p}`}
                  current={page === p}
                  className={cn(
                    "rounded-lg",
                    page === p && "text-white",
                  )}
                  style={
                    page === p
                      ? { backgroundColor: "var(--brand-button-bg, var(--brand-primary))" }
                      : { color: "var(--brand-text)" }
                  }
                >
                  {p}
                </PageLink>
              ),
            )}
          </div>
          <PageButton
            label="Next page"
            icon="chevron_right"
            disabled={page === totalPages}
            href={pageHref(Math.min(totalPages, page + 1))}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          />
          <PageButton
            label="Last page"
            icon="last_page"
            disabled={page === totalPages}
            href={pageHref(totalPages)}
            onClick={() => onPageChange(totalPages)}
          />
        </Pagination>
      )}
    </div>
  );
}

function AgentGridSection({
  heading,
  headingId,
  agents,
  variant,
  testId,
  agentDetailPath,
}: {
  heading: string;
  headingId: string;
  agents: AgentCardAgent[];
  variant: AdAgentResultsPhase;
  testId: string;
  agentDetailPath: string;
}) {
  return (
    <section aria-labelledby={headingId}>
      <h2
        id={headingId}
        className="mb-5 text-xl font-bold tracking-[-0.025em]"
        style={{ color: "var(--brand-text)" }}
      >
        {heading}
      </h2>
      <div
        data-testid={testId}
        className={cn(
          "grid grid-cols-1 gap-3 sm:grid-cols-2",
          variant === "featured"
            ? "md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            : "lg:grid-cols-4",
        )}
      >
        {agents.map((agent) => (
          <AgentCard
            key={agent.agentId}
            agent={agent}
            variant={variant}
            agentDetailPath={agentDetailPath}
          />
        ))}
      </div>
    </section>
  );
}

function PageButton({
  label,
  icon,
  disabled,
  onClick,
  href,
}: {
  label: string;
  icon: string;
  disabled: boolean;
  onClick: () => void | boolean;
  href: string;
}) {
  return (
    <PageLink
      href={href}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-lg border"
      style={{
        borderColor: "var(--brand-card-border)",
        color: "var(--brand-text-muted)",
        backgroundColor: "var(--brand-card-bg)",
      }}
    >
      <Icon name={icon} size={18} />
    </PageLink>
  );
}

/** Real links preserve browser defaults (new tabs, no JS); plain clicks use the
 * existing navigation controller. Disabled controls have no navigable URL. */
function PageLink({ href, disabled, current, onClick, className, ...props }: Omit<React.ComponentProps<"a">, "onClick"> & {
  disabled?: boolean;
  current?: boolean;
  onClick: () => void | boolean;
}) {
  return <a {...props} role={disabled ? "link" : undefined} href={disabled ? undefined : href}
    aria-disabled={disabled || undefined} aria-current={current ? "page" : undefined}
    tabIndex={disabled ? -1 : undefined}
    className={cn(paginationControlVariants({ current }), className)}
    onClick={(event) => {
      if (disabled) { event.preventDefault(); return; }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onClick();
    }} />;
}
